import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["Installer", "Merchant", "Investor", "Industry Partner", "Other"];

const inputCls =
  "w-full rounded-full border border-slate-300 bg-white px-5 py-3 text-sm text-[#0a1f33] placeholder:text-slate-400 focus:outline-none focus:border-[#1c9ce8] focus:ring-2 focus:ring-[#1c9ce8]/25 transition-[border-color,box-shadow] duration-300";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", role: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        role: form.role,
        phone: "",
        company: "",
        message: "Registered interest via the Verixx H2O website.",
      });
      toast.success("Interest registered. We'll keep you updated on the pilot.", {
        description: "Verixx H2O // LeakLock Limited",
      });
      setForm({ name: "", email: "", role: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email luke@leaklock.uk.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-20 sm:py-24 scroll-mt-16" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-display font-extrabold uppercase leading-[1.05] tracking-tight text-4xl sm:text-5xl text-[#0a1f33]">
              Interested In
              <br />
              LeakLock?
            </h2>
            <span className="block w-12 h-1 bg-[#1c9ce8] mt-6" />
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md" data-testid="contact-body">
              Whether you're an installer, merchant, industry partner or investor,
              register your interest and we'll keep you updated as we move towards
              launch.
            </p>

            <div className="mt-10 space-y-3.5" data-testid="contact-channels">
              <a href="mailto:luke@leaklock.uk.com" className="group flex items-center gap-3 text-sm text-slate-600 hover:text-[#1c9ce8] transition-colors duration-300" data-testid="contact-email-channel">
                <Mail className="w-4 h-4 text-[#1c9ce8]" />
                luke@leaklock.uk.com
              </a>
              <a href="tel:+447825187785" className="group flex items-center gap-3 text-sm text-slate-600 hover:text-[#1c9ce8] transition-colors duration-300" data-testid="contact-phone-channel">
                <Phone className="w-4 h-4 text-[#1c9ce8]" />
                07825 187 785
              </a>
              <div className="flex items-center gap-3 text-sm text-slate-600" data-testid="contact-address-channel">
                <MapPin className="w-4 h-4 text-[#1c9ce8]" />
                12A The Square, Wickham, PO17 5JQ
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submit}
            data-testid="contact-form"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Name"
                aria-label="Name"
                className={inputCls}
                data-testid="contact-input-name"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                aria-label="Email address"
                className={inputCls}
                data-testid="contact-input-email"
              />
              <select
                required
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                aria-label="I am a..."
                className={`${inputCls} appearance-none cursor-pointer ${form.role ? "" : "text-slate-400"}`}
                data-testid="contact-select-role"
              >
                <option value="" disabled>
                  I am a...
                </option>
                {ROLES.map((r) => (
                  <option key={r} value={r} className="text-[#0a1f33]">
                    {r}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={sending}
                className="shrink-0 flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-[#1c9ce8] hover:bg-[#3ab0f5] disabled:opacity-60 text-white text-sm font-semibold shadow-[0_8px_30px_rgba(28,156,232,0.3)] transition-[background-color,opacity] duration-300"
                data-testid="contact-submit-button"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {sending ? "Sending..." : "Register Interest"}
                {!sending && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-400" data-testid="contact-consent-note">
              By submitting, you agree to be contacted about LeakLock and Verixx H2O.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
